import {
  Box,
  Card,
  CardActionArea,
  Chip,
  CircularProgress,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import {
  CategoryOutlined,
  ChevronRight,
  SearchOutlined,
} from "@mui/icons-material";

import { useUIStore } from "../../stores/UIStore";
import { TArtifact } from "../../types";
import { snakeToTitleCase } from "src/reusable/Functions";

type TAtomAssistArtifactList = {
  artifacts: TArtifact[];
};

// Get a friendly display name for the tool
function getToolDisplayName(toolName: string): string {
  const toolNameLower = toolName.toLowerCase();
  if (toolNameLower.includes("search") || toolNameLower.includes("nuclearn")) {
    return "Search Results";
  }
  return snakeToTitleCase(toolName);
}

// Get icon based on tool type
function getToolIcon(toolName: string) {
  const toolNameLower = toolName.toLowerCase();
  if (toolNameLower.includes("search") || toolNameLower.includes("nuclearn")) {
    return SearchOutlined;
  }
  return CategoryOutlined;
}

// Get result count from artifact content
function getResultCount(artifact: TArtifact): number | null {
  const content = artifact.content?.text;
  if (!content) return null;

  // Handle tool_result wrapper
  const result =
    typeof content === "object" && content.message_type === "tool_result"
      ? content.result
      : content;

  // If result is an array, return its length
  if (Array.isArray(result)) {
    return result.length;
  }

  // NOTE: I'm unsure of why we'd want to do this as
  // "no results" could be a valid string result from some tools.
  // If result is "No Results" string, return 0
  // Only match if the entire string (or trimmed) is essentially "No Results"
  if (typeof result === "string") {
    const trimmedLower = result.trim().toLowerCase();
    // Match exact phrases like "no results" or "no results found"
    if (
      trimmedLower === "no results" ||
      trimmedLower === "no results found" ||
      trimmedLower === "no results found."
    ) {
      return 0;
    }
  }

  return null;
}

// Get preview text from artifact content
function getPreviewText(artifact: TArtifact): string | null {
  const content = artifact.content?.text;
  if (!content) return null;

  const result =
    typeof content === "object" && content.message_type === "tool_result"
      ? content.result
      : content;

  if (typeof result === "string") {
    return result;
  }

  if (Array.isArray(result) && result.length > 0 && result[0]?.content) {
    // Get first result's content as preview
    const firstContent = result[0].content;
    if (typeof firstContent === "string") {
      return firstContent.length > 100
        ? firstContent.substring(0, 100) + "..."
        : firstContent;
    }
  }

  return null;
}

interface SidebarArtifactCardProps {
  artifact: TArtifact;
  onClick: () => void;
}

const SidebarArtifactCard = ({
  artifact,
  onClick,
}: SidebarArtifactCardProps) => {
  const theme = useTheme();
  const Icon = getToolIcon(artifact.type);
  const displayName = getToolDisplayName(artifact.type);
  const resultCount = getResultCount(artifact);
  const previewText = getPreviewText(artifact);
  const isLoading = artifact.isLoading;

  return (
    <Card
      data-testid={`sidebar-artifact-card-${artifact.id}`}
      variant="outlined"
      sx={{
        borderRadius: 2,
        overflow: "hidden",
        transition: "all 0.15s ease",
        "&:hover": {
          borderColor: theme.palette.primary.main,
          boxShadow: `0 2px 8px ${theme.palette.action.hover}`,
        },
      }}
    >
      <CardActionArea onClick={onClick} sx={{ p: 0 }}>
        <Box sx={{ p: 2 }}>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
          >
            <Stack direction="row" alignItems="center" gap={1.5}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: 36,
                  height: 36,
                  borderRadius: 1.5,
                  bgcolor: isLoading
                    ? theme.palette.action.hover
                    : theme.palette.primary.main + "14",
                }}
              >
                {isLoading ? (
                  <CircularProgress size={18} />
                ) : (
                  <Icon
                    sx={{
                      fontSize: 20,
                      color: theme.palette.primary.main,
                    }}
                  />
                )}
              </Box>
              <Box>
                <Typography
                  variant="subtitle2"
                  data-testid={`artifact-card-name-${artifact.id}`}
                  sx={{ fontWeight: 600, color: "text.primary" }}
                >
                  {displayName}
                </Typography>
                <Typography
                  variant="caption"
                  data-testid={`artifact-card-result-count-${artifact.id}`}
                  sx={{ color: "text.secondary" }}
                >
                  {isLoading
                    ? "Processing..."
                    : resultCount !== null
                      ? `${resultCount} result${resultCount !== 1 ? "s" : ""}`
                      : "View details"}
                </Typography>
              </Box>
            </Stack>
            <Stack direction="row" alignItems="center" gap={1}>
              {!isLoading && resultCount === 0 && (
                <Chip
                  label="No results"
                  size="small"
                  sx={{
                    height: 22,
                    fontSize: "0.7rem",
                    bgcolor: theme.palette.warning.light + "30",
                    color: theme.palette.warning.dark,
                  }}
                />
              )}
              <ChevronRight sx={{ color: "text.disabled", fontSize: 20 }} />
            </Stack>
          </Stack>

          {/* Preview text */}
          {!isLoading && previewText && resultCount !== 0 && (
            <Typography
              variant="body2"
              sx={{
                mt: 1.5,
                color: "text.secondary",
                fontSize: "0.813rem",
                lineHeight: 1.5,
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {previewText}
            </Typography>
          )}
        </Box>
      </CardActionArea>
    </Card>
  );
};

const ArtifactCardList = ({ artifacts }: TAtomAssistArtifactList) => {
  const { openArtifact } = useUIStore();

  return (
    <Box data-testid="artifact-card-list">
      <Stack
        data-testid="artifact-card-list-items"
        direction="column"
        spacing={1.5}
      >
        {artifacts.map((artifact) => (
          <SidebarArtifactCard
            key={artifact.id}
            artifact={artifact}
            onClick={() => openArtifact(artifact.id)}
          />
        ))}
      </Stack>
    </Box>
  );
};

export default ArtifactCardList;
